import { CheckCircle, CircleAlert, TriangleAlert, Info, X } from "lucide-react"
import {
  ComponentPropsWithoutRef,
  createElement,
  FunctionComponent,
  ReactNode,
  useEffect,
  useMemo,
} from "react"
import { getUserMessage, isApiError } from "shared-ts/api/apiError"
import { useUserVisibleErrorLogger } from "shared-ts/hooks/useUserVisibleErrorLogger"

import { classNames } from "../../../../lib/classNames"
import { IconProps } from "../../../Icon"

const BannerModes = {
  ERROR: "error",
  SUCCESS: "success",
  WARNING: "warning",
  INFO: "information",
  INFO_SUBTLE: "information-subtle",
  COPY: "copy",
} as const
type BannerMode = (typeof BannerModes)[keyof typeof BannerModes]

// Controls in a react-hot-toast custom toast must opt back into pointer events:
// the Toaster container sets pointer-events:none and only react-hot-toast's own
// ToastBar re-enables it, so without this the dismiss and action are unclickable.
const clickableInToast = "pointer-events-auto"

/**
 * @deprecated Use the BannerV2 component instead. Props for the legacy banner,
 * which only lib/toast.tsx still renders until the new Toast ships.
 */
export interface LegacyBannerProps extends ComponentPropsWithoutRef<"div"> {
  message: ReactNode
  mode: BannerMode
  onClose?: () => void
  onClickActionButton?: () => void
  actionButtonMessage?: string
  actionButtonMode?: "inline" | "block"
  error?: unknown
  loggerScope?: string
  /**
   * How many times this same message arrived while it was on screen, shown after
   * the message when above one. Kept out of `message` on purpose: it is display
   * detail, and folding it in would fragment error logging, where one message
   * would otherwise aggregate as itself plus a variant per count. Only
   * lib/toast.tsx sets this.
   */
  repeatCount?: number
}

/**
 * @deprecated Use the BannerV2 component instead. Primary component for user
 * feedback, especially error surfaces. Logs errors automatically and extracts
 * user-friendly messages from ApiErrors. Only lib/toast.tsx still renders this,
 * until the new Toast ships.
 */
export const LegacyBanner: FunctionComponent<LegacyBannerProps> = ({
  className,
  mode,
  message,
  onClickActionButton,
  actionButtonMessage,
  actionButtonMode,
  onClose,
  error,
  loggerScope,
  repeatCount,
  ...otherProps
}) => {
  /* Attempt to override default string message with ApiError's userMessage. */
  const overriddenMessage = useMemo(() => {
    if (typeof message !== "string") return undefined

    // Extract userMessage from ApiError if available, otherwise use original message
    return (isApiError(error) && getUserMessage(error)) || message
  }, [message, error])

  /* Wrap Error locally to capture the call site stack trace. */
  const wrappedError = useMemo(() => {
    if (!error) return undefined

    const wError = new Error(
      error instanceof Error ? error.message : String(error),
      { cause: error }
    )
    if (error instanceof Error) wError.name = error.name

    return wError
  }, [error])

  const { error: logUserVisibleError, info: logUserVisibleInfo } =
    useUserVisibleErrorLogger(loggerScope ? `banner.${loggerScope}` : "banner")

  // Log error level when Banner mode is "error" and an error exists
  useEffect(() => {
    if (mode != "error" || !wrappedError) return

    logUserVisibleError(
      wrappedError,
      overriddenMessage ?? "Unknown user error message"
    )
  }, [mode, wrappedError, overriddenMessage, logUserVisibleError])

  // Log info level when Banner mode is "error" but no error exists
  useEffect(() => {
    if (mode !== "error" || wrappedError) return

    logUserVisibleInfo(
      "Error Banner shown without an Error object",
      overriddenMessage ?? "Unknown user error message"
    )
  }, [mode, wrappedError, overriddenMessage, logUserVisibleInfo])

  const repeats = repeatCount && repeatCount > 1 ? ` (${repeatCount})` : ""

  return (
    <div
      data-testid={`${mode}-banner`}
      className={classNames(
        "rounded-surface p-3",
        containerClassNames[mode],
        className
      )}
      aria-label={
        (overriddenMessage ??
          (typeof message === "string" ? message : "Banner")) + repeats
      }
      {...otherProps}
    >
      <div
        className={classNames(
          "text-body flex flex-row items-center",
          actionButtonMessage ? "justify-start" : "justify-between"
        )}
      >
        <div className="flex flex-row items-center gap-x-3">
          <div className="shrink-0 self-start">
            {createElement(icon[mode], {
              className: classNames(
                "h-6 w-6 text-v2-foreground-default",
                mode === BannerModes.INFO_SUBTLE &&
                  "text-v2-foreground-information",
                mode === "copy" && "text-v2-foreground-success"
              ),
            })}
          </div>
          <div className={`flex-1 ${mode} whitespace-pre-line`}>
            {overriddenMessage ?? message}
            {repeats}
            {actionButtonMode === "inline" && (
              <span
                className={classNames(
                  "ml-1 cursor-pointer underline",
                  clickableInToast
                )}
                onClick={onClickActionButton}
              >
                {actionButtonMessage}
              </span>
            )}
          </div>
        </div>
        {actionButtonMode === "block" && (
          <div
            className={classNames(
              "ml-auto cursor-pointer underline",
              clickableInToast
            )}
            onClick={onClickActionButton}
          >
            {actionButtonMessage}
          </div>
        )}
        {!!onClose && (
          <button
            id="banner-button"
            aria-label="Dismiss"
            data-testid="banner-close-button"
            type="button"
            className={classNames(
              "text-v2-foreground-default h-6 w-6",
              clickableInToast,
              mode === BannerModes.INFO_SUBTLE &&
                "text-v2-foreground-information",
              actionButtonMode === "block" && "ml-1",
              actionButtonMode === "inline" && "ml-auto"
            )}
            onClick={onClose}
          >
            <X />
          </button>
        )}
      </div>
    </div>
  )
}

const containerClassNames: Record<BannerMode, string> = {
  [BannerModes.ERROR]: "bg-danger-default",
  [BannerModes.SUCCESS]: "bg-success-default",
  [BannerModes.WARNING]: "bg-warning-default",
  [BannerModes.INFO]: "bg-information-default",
  [BannerModes.INFO_SUBTLE]:
    "bg-v2-background-information-weak border border-v2-background-information-tonal-strong text-v2-foreground-information-tonal",
  [BannerModes.COPY]:
    "bg-v2-background-elevation-raised border border-v2-border-default shadow-control",
}

const icon: Record<BannerMode, IconProps["icon"]> = {
  [BannerModes.ERROR]: CircleAlert,
  [BannerModes.SUCCESS]: CheckCircle,
  [BannerModes.WARNING]: TriangleAlert,
  [BannerModes.INFO]: Info,
  [BannerModes.INFO_SUBTLE]: Info,
  [BannerModes.COPY]: CheckCircle,
}
